/* import __COLOCATED_TEMPLATE__ from './contact.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import Component from '@glimmer/component';
import { post } from 'embercom/lib/ajax';
import { action } from '@ember/object';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';

interface Args {}

export default class Contact extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: { app: { id: string } };
  @service declare intl: IntlService;
  @service declare notificationsService: {
    notifyConfirmation: (message: string) => void;
    notifyResponseError: (e: Error, messages: { default: string }) => void;
  };

  get app() {
    return this.appService.app;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get selectedIdentityMappingContactField() {
    return this.contactFieldOptions.find(
      (option) => option.value === this.salesforceConfig.identityMappingContactField,
    );
  }

  get selectedField(): ExternalFieldIdentifier | undefined {
    return this.contactFields.find(
      (field) => field.externalId === this.salesforceConfig.identityMappingContactField,
    );
  }

  get contactFieldOptions() {
    return this.contactFields
      .filter((field) => !field.hiddenForIdentityMapping)
      .map((field) => ({
        text: field.name,
        value: field.externalId,
        isDisabled: field.disabledForIdentityMapping,
        tooltipText:
          field.disabledForIdentityMapping &&
          this.intl.t('standalone.salesforce.record-matching.contact.non-filterable-field-warning'),
      }));
  }

  get salesforceConfig() {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get contactFields() {
    return this.salesforcePlatform.userFields;
  }

  @action async onContactFieldSelect(value: string) {
    this.salesforceConfig.identityMappingContactField = value;
    await this.saveIdentityMappingFields();
  }

  async saveIdentityMappingFields() {
    try {
      await post(
        `/ember/standalone/salesforce_configurations/${this.app.id}/update_identity_mapping_fields`,
        {
          identity_mapping_contact_field: this.salesforceConfig.identityMappingContactField,
        },
      );

      // replace this message by adding a busy state to dropdowns
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.record-matching.contact.success'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, { default: error.message });
      this.salesforceConfig.rollbackAttributes();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::ManageData::RecordMatching::Contact': typeof Contact;
  }
}
